<script setup>
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  Legend
} from 'chart.js'
import { onMounted, ref, watch } from 'vue'
import { Scatter } from 'vue-chartjs'
import { mooringChartScaleConfig, mooringChartConfig } from './chartShipConfig'
import { storeToRefs } from 'pinia/dist/pinia'
import { computed } from 'vue'

import { useFairleadStore } from '@/stores/fairlead'
import { useCalculationStore } from '@/stores/calculation'
import { useMooringPointStore } from '@/stores/mooring_point'
import { useWinchStore } from '@/stores/winch'
import { useShipContourStore } from '@/stores/ship_contour'
import { useShipStore } from '@/stores/ship'
import zoomPlugin from 'chartjs-plugin-zoom'
import datalabelPlugin from 'chartjs-plugin-datalabels'
import hexToRgbA from '../../utils/hex_to_rgba'
import { useEventsBus } from '@haskoning/vue-component-library'
import { FAIRLEAD_LABEL, WINCH_LABEL } from '@/constants/shipConstants'
import ChartControls from './ChartControls.vue'
import { ISLAND } from '@/components/calculation/calculation.constant'
import {
  BERTH_TYPE,
  PLATFORM_LENGTH,
  PLATFORM_WIDTH,
  X1,
  X2,
  Y_LOCATION_PLATFORM
} from '@/constants/calculationFieldConstants'
import { ECCENTRICITY, LENGTH_OVERALL } from '@/constants/shipFieldConstants'

const props = defineProps({
  height: {
    type: String
  }
})

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, Filler, zoomPlugin)

const fairleadStore = useFairleadStore()
const mooringPointStore = useMooringPointStore()

const { items: shipItems } = storeToRefs(useShipStore())
const { currentItem: currentCalculation } = storeToRefs(useCalculationStore())
const { items: fairleadItems, getMooringLines: mooringLines } = storeToRefs(fairleadStore)
const { items: winchItems } = storeToRefs(useWinchStore())
const { items: shipContourItems } = storeToRefs(useShipContourStore())
const { getPlot: mooringPoints } = storeToRefs(mooringPointStore)

const chartArea = ref(null)
const mooringLayoutChart = ref(null)
const chartRatio = ref(1)

const { bus } = useEventsBus()

watch(
  () => bus.value.get('chartEvent'),
  (val) => {
    const [item] = val
    triggerMouseEvent(item)
  }
)

// Make mooring point changes reactive in graph
mooringPointStore.$subscribe((mutation, state) => {
  mooringLayoutChart.value.chart.update()
})

const getColor = (context, opacity, objects, field = 'color') => {
  const index = context.dataIndex
  const item = objects[index]
  return item && item[field] ? hexToRgbA(item[field], opacity) : undefined
}

const getShipContourData = (ship, contours) => {
  return {
    label: false,
    hover: {
      mode: false
    },
    pointRadius: 0,
    borderWidth: 1,
    animation: false,
    showLine: true,
    borderColor: `rgba(0,0,0,${ship.opacity})`,
    pointBackgroundColor: 'black',
    data: contours.map((item) => {
      return { x: item.x + ship[ECCENTRICITY], y: item.y }
    })
  }
}

const shipContours = computed(() => {
  const contourLines = []
  shipContourItems.value && shipItems.value
    ? shipItems.value.forEach((ship) => {
        if (ship.hide) {
          return null
        }
        const contours = shipContourItems.value.filter(
          (shipContourItem) => ship.id === shipContourItem.ship
        )
        const shipContour = getShipContourData(ship, contours)
        contourLines.push(shipContour)
      })
    : null
  return contourLines
})

const getShipFairleadData = (ship, fairleads) => {
  return {
    label: FAIRLEAD_LABEL + ship.id,
    fill: false,
    animation: false,
    backgroundColor: (context) => getColor(context, ship.opacity, fairleads, 'winch_color'),
    pointBackgroundColor: (context) => getColor(context, ship.opacity, fairleads, 'winch_color'),
    data: fairleads.map((item) => {
      return {
        x: ship[ECCENTRICITY] + item.x,
        y: item.y,
        id: item.id,
        winch_color: item.winch_color
      }
    })
  }
}

const fairleads = computed(() => {
  let fairleadPoints = []
  fairleadItems.value && shipItems.value
    ? shipItems.value.forEach((ship) => {
        if (ship.hide) {
          return null
        }
        const fairleads = fairleadItems.value.filter(
          (fairleadItem) => ship.id === fairleadItem.ship
        )
        const shipFairlead = getShipFairleadData(ship, fairleads)
        fairleadPoints.push(shipFairlead)
      })
    : null
  return fairleadPoints
})

const getShipWinchData = (ship, winches) => {
  return {
    label: WINCH_LABEL + ship.id,
    fill: false,
    animation: false,
    pointStyle: 'rect',
    borderColor: (context) => getColor(context, ship.opacity, winches),
    backgroundColor: (context) => getColor(context, ship.opacity, winches),
    pointBackgroundColor: (context) => getColor(context, ship.opacity, winches),
    data: winches
      ? winches.map((item) => {
          return {
            x: ship[ECCENTRICITY] + item.x,
            y: item.y,
            id: item.id,
            color: item.winch_color
          }
        })
      : []
  }
}

const winches = computed(() => {
  let winchPoints = []

  winchItems.value && shipItems.value
    ? shipItems.value.forEach((ship) => {
        if (ship.hide) {
          return null
        }
        const winches = winchItems.value.filter((winchItem) => ship.id === winchItem.ship)
        const shipWinch = getShipWinchData(ship, winches)
        winchPoints.push(shipWinch)
      })
    : null
  return winchPoints
})

const shipLabels = computed(() => {
  const shipLabels = []
  shipContourItems.value && shipItems.value
    ? shipItems.value.forEach((ship) => {
        if (ship.hide) {
          return null
        }
        const shipLabel = {
          label: 'Capacity ' + ship.capacity,
          objType: 'capacity',
          pointBackgroundColor: hexToRgbA('#000000', ship.opacity),
          pointRadius: 0,
          animation: false
        }
        shipLabels.push(shipLabel)
      })
    : null
  return shipLabels
})

const platform = computed(() => {
  const calculation = currentCalculation.value
  const x1 =
    calculation[BERTH_TYPE] === ISLAND ? calculation[X1] : -calculation[PLATFORM_LENGTH] / 2
  const x2 = calculation[BERTH_TYPE] === ISLAND ? calculation[X2] : calculation[PLATFORM_LENGTH] / 2

  const platformLine = (fill = false, data = []) => {
    return {
      label: `Platform`,
      pointRadius: 2,
      borderWidth: 1,
      animation: false,
      fill: fill,
      showLine: true,
      data: data
    }
  }

  return [
    platformLine(false, [
      [x1, calculation[Y_LOCATION_PLATFORM]],
      [x2, calculation[Y_LOCATION_PLATFORM]]
    ]),
    platformLine('-1', [
      [x1, calculation[Y_LOCATION_PLATFORM] - calculation[PLATFORM_WIDTH]],
      [x2, calculation[Y_LOCATION_PLATFORM] - calculation[PLATFORM_WIDTH]]
    ])
  ]
})

function triggerMouseEvent({ id, label }) {
  const chartJs = mooringLayoutChart.value.chart
  const index = chartJs.data.datasets.findIndex((item) => item.label === label)
  const data = chartJs.data.datasets.find((item) => item.label === label)
  const itemIndex = data.data.findIndex((item) => id === item.id)
  const meta = chartJs.getDatasetMeta(index),
    rect = chartJs.canvas.getBoundingClientRect(),
    point = meta.data[itemIndex].getCenterPoint(),
    evt = new MouseEvent('mousemove', {
      clientX: rect.left + point.x,
      clientY: rect.top + point.y
    }),
    node = chartJs.canvas
  node.dispatchEvent(evt)
}

function resetZoom() {
  mooringLayoutChart.value.chart.resetZoom()
}

const largestShip = computed(() => {
  const lengths = shipItems.value.map((item) => item[LENGTH_OVERALL])
  const maxLength = Math.max(...lengths)
  return shipItems.value.find((item) => item[LENGTH_OVERALL] === maxLength)
})

const dataSets = computed(() => {
  return {
    datasets: [
      mooringPoints.value,
      ...fairleads.value,
      ...shipLabels.value,
      ...winches.value,
      ...mooringLines.value,
      ...shipContours.value,
      ...platform.value
    ]
  }
})

const chartScales = computed(() => {
  return largestShip.value ? largestShip.value[LENGTH_OVERALL] / 2 + 25 : 100
})

const chartOptions = computed(() => {
  const scales = { ...mooringChartScaleConfig }
  scales.x = {
    ...mooringChartScaleConfig.x,
    suggestedMin: -chartScales.value,
    suggestedMax: chartScales.value,
    stepSize: 10
  }
  scales.y = {
    ...mooringChartScaleConfig.y,
    suggestedMin: -chartScales.value * chartRatio.value,
    suggestedMax: chartScales.value * chartRatio.value,
    stepSize: 10
  }
  return { ...mooringChartConfig, scales }
})

onMounted(() => {
  chartRatio.value = chartArea.value.clientHeight / chartArea.value.clientWidth
})

const background_color_plugin = {
  id: 'background_color_plugin',
  beforeDraw: (chart, args, options) => {
    const {
      ctx,
      chartArea: { top, right, bottom, left, width, height }
    } = chart
    ctx.save()
    ctx.globalCompositeOperation = 'destination-over'
    ctx.fillStyle = 'white'
    ctx.fillRect(left, top, width, height)
    ctx.restore()
  }
}

const resizeChart = () => {
  if (mooringLayoutChart.value) {
    mooringLayoutChart.value.chart.resize()
  }
}

defineExpose({
  resizeChart
})
</script>
<template>
  <div
    v-if="dataSets.datasets.length"
    ref="chartArea"
    class="d-flex flex-column"
    :style="{ height: height }"
  >
    <ChartControls @resetZoom="resetZoom()"></ChartControls>

    <Scatter
      id="mooringLayoutChart"
      ref="mooringLayoutChart"
      :data="dataSets"
      :options="chartOptions"
      :plugins="[background_color_plugin, datalabelPlugin]"
    >
    </Scatter>
  </div>
</template>

<style scoped>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}
</style>
